/* global axios */
import ApiClient from './ApiClient';

class CustomReportsAPI extends ApiClient {
  constructor() {
    super('custom_reports', { accountScoped: true });
  }

  duplicate(id) {
    return axios.post(`${this.url}/${id}/duplicate`);
  }

  archive(id) {
    return axios.post(`${this.url}/${id}/archive`);
  }

  restore(id) {
    return axios.post(`${this.url}/${id}/restore`);
  }

  favorite(id) {
    return axios.post(`${this.url}/${id}/favorite`);
  }

  unfavorite(id) {
    return axios.delete(`${this.url}/${id}/favorite`);
  }

  getReport(id) {
    return axios.get(`${this.url}/${id}`);
  }

  catalog() {
    return axios.get(`${this.url}/catalog`);
  }

  validateQuery(queryDefinition) {
    return axios.post(`${this.url}/query_validation`, {
      query_definition: queryDefinition,
    });
  }

  getWidgets(reportId) {
    return axios.get(`${this.url}/${reportId}/widgets`);
  }

  getWidget(reportId, id) {
    return axios.get(`${this.url}/${reportId}/widgets/${id}`);
  }

  createWidget(reportId, widget) {
    return axios.post(`${this.url}/${reportId}/widgets`, widget);
  }

  updateWidget(reportId, id, widget) {
    return axios.put(`${this.url}/${reportId}/widgets/${id}`, widget);
  }

  deleteWidget(reportId, id) {
    return axios.delete(`${this.url}/${reportId}/widgets/${id}`);
  }

  previewWidget(reportId, id) {
    return axios.post(`${this.url}/${reportId}/widgets/${id}/preview`);
  }

  previewQuery(reportId, queryDefinition) {
    return axios.post(`${this.url}/${reportId}/widgets/preview_query`, {
      query_definition: queryDefinition,
    });
  }

  batchPreview(reportId, crossFilters = null) {
    const config = crossFilters ? { cross_filters: crossFilters } : null;
    return axios.post(`${this.url}/${reportId}/widgets/batch_preview`, config);
  }

  reorderWidgets(reportId, items) {
    return axios.post(`${this.url}/${reportId}/widgets/reorder`, { items });
  }

  runReport(reportId, parameters = {}) {
    return axios.post(`${this.url}/${reportId}/run`, { parameters });
  }

  getRuns(reportId) {
    return axios.get(`${this.url}/${reportId}/runs`);
  }

  getRun(reportId, runId) {
    return axios.get(`${this.url}/${reportId}/runs/${runId}`);
  }

  cancelRun(reportId, runId) {
    return axios.post(`${this.url}/${reportId}/runs/${runId}/cancel`);
  }

  createExport(reportId, { file_format = 'csv', parameters = {} } = {}) {
    return axios.post(`${this.url}/${reportId}/exports`, {
      file_format,
      parameters,
    });
  }

  getExports(reportId) {
    return axios.get(`${this.url}/${reportId}/exports`);
  }

  getExport(reportId, exportId) {
    return axios.get(`${this.url}/${reportId}/exports/${exportId}`);
  }

  downloadExport(reportId, exportId) {
    return axios.get(`${this.url}/${reportId}/exports/${exportId}/download`, {
      responseType: 'blob',
    });
  }

  getAlerts(reportId) {
    return axios.get(`${this.url}/${reportId}/alerts`);
  }

  createAlert(reportId, alert) {
    return axios.post(`${this.url}/${reportId}/alerts`, alert);
  }

  updateAlert(reportId, id, alert) {
    return axios.patch(`${this.url}/${reportId}/alerts/${id}`, alert);
  }

  deleteAlert(reportId, id) {
    return axios.delete(`${this.url}/${reportId}/alerts/${id}`);
  }

  testAlert(reportId, id) {
    return axios.post(`${this.url}/${reportId}/alerts/${id}/test`);
  }

  // Goals
  getGoals(reportId) {
    return axios.get(`${this.url}/${reportId}/goals`);
  }

  createGoal(reportId, goal) {
    return axios.post(`${this.url}/${reportId}/goals`, goal);
  }

  updateGoal(reportId, id, goal) {
    return axios.patch(`${this.url}/${reportId}/goals/${id}`, goal);
  }

  deleteGoal(reportId, id) {
    return axios.delete(`${this.url}/${reportId}/goals/${id}`);
  }

  evaluateGoal(reportId, id) {
    return axios.post(`${this.url}/${reportId}/goals/${id}/evaluate`);
  }

  evaluateAllGoals(reportId) {
    return axios.post(`${this.url}/${reportId}/goals/evaluate_all`);
  }
}

export default new CustomReportsAPI();
